import {BaseDalService} from "@hec/core";
import {AppConfiguration, ApplicationService} from "@hec/models";
import axios, {AxiosInstance, CancelTokenSource} from "axios";
import {OrderDto, SectionFragmentSide, SectionFragmentType} from "@hec/api-dtos";
import _ from "lodash";
import {HashObject} from "@hec/utils";
import {getSectionFragmentsFromOrder, getSouthFaceFromOrder} from "../Util";


export class PutOrderService extends BaseDalService implements ApplicationService {

  private lastPutHash: string | undefined;
  private client: AxiosInstance;
  public static override serviceName = Symbol('PutOrderService');
  private cancelTokenSource: CancelTokenSource | undefined;

  constructor() {
    super();
    this.client = axios.create({});
  }

  override configure(configuration: AppConfiguration) {
    this.client = axios.create({
      baseURL: `${configuration.baseUrl}/api`,
    });

    this.useDateTimeConverters(this.client);
    this.useEnumConverters(this.client);
  }


  getName(): symbol {
    return PutOrderService.serviceName;
  }

  static override getName(): symbol {
    return PutOrderService.serviceName;
  }

  hashObject(obj: any): string {
    return HashObject(obj);
  }



  async putOrder(orderInput: OrderDto, orderConfigurationCode: string): Promise<PutOrderResult | null> {
    this.configureAuthInterceptor(this.client, orderConfigurationCode);

    function makeAllPropertiesWritable<T>(obj: T): T {
      if (typeof obj !== 'object' || obj === null) {
        return obj;
      }

      Object.keys(obj).forEach(key => {
        const descriptor = Object.getOwnPropertyDescriptor(obj, key);
        if (descriptor && !descriptor.writable) {
          Object.defineProperty(obj, key, {
            ...descriptor,
            writable: true
          });
        }

        // Recurse into nested objects
        const value = (obj as any)[key];
        if (typeof value === 'object' && value !== null) {
          makeAllPropertiesWritable(value);
        }
      });

      return obj;
    }

    const order: OrderDto = _.cloneDeep(orderInput);

    makeAllPropertiesWritable(order);

    let sectionFragments = getSectionFragmentsFromOrder(order)


    sectionFragments = sectionFragments.map(sf=> {
      sf.availableDormerMaterials = [];
      sf.configuredDormerColor = null;

      sf.availableDormerMaterialsSideRods = [];
      sf.configuredDormerColorSideRods = null;

      if(sf.copiedFrom) {
        sf.copiedFrom.availableDormerMaterials = [];
        sf.copiedFrom.configuredDormerColor = null;

        sf.availableDormerMaterialsSideRods = [];
        sf.configuredDormerColorSideRods = null;
      }
      return sf;
    });

    const southFace = getSouthFaceFromOrder(order);

    southFace!.sectionFragments = sectionFragments;

    // Debug: Detect invalid splinters being sent to backend
    const invalidSplinters = sectionFragments.filter(sf =>
      sf.sectionFragmentType === SectionFragmentType.Splinter &&
      (sf as any).splinterSide == SectionFragmentSide.West &&
      sf.width === 50
    );

    if (invalidSplinters.length > 0) {
      console.error('🔴 SENDING INVALID SPLINTER TO BACKEND', {
        count: invalidSplinters.length,
        splinters: invalidSplinters.map(s => ({
          id: s.id,
          tempId: s.tempId,
          splinterSide: (s as any).splinterSide,
          splinterSideString: SectionFragmentSide[(s as any).splinterSide],
          width: s.width,
          height: s.height,
          copiedFromId: s.copiedFromId,
          belongsToUser: s.belongsToUser
        })),
        orderId: order.id,
        stackTrace: new Error().stack
      });
    }


    const newSectionFragmentIds = sectionFragments.filter(x=> x.id != null).map(x=> x.id);
    const duplicatIdSectionFragments = sectionFragments.filter(x=> newSectionFragmentIds.filter(id => x.id == id).length > 1);

    if(duplicatIdSectionFragments.length > 0) {
      console.error("DUPLICATE ID REPLACEMENT ERR!");
    }


    const curOrderHash = this.hashObject(order);

    if (curOrderHash === this.lastPutHash) {
      return {
        order,
        modified: false,
      };
    }

    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('New order request initiated');
    }

    this.cancelTokenSource = axios.CancelToken.source();
    this.lastPutHash = curOrderHash;

    try {
      const response = await this.client.put(`/orders/${order.id}`, order, {
        cancelToken: this.cancelTokenSource.token,
      });

      if (response.status === 200) {
        const order = response.data as OrderDto;


        const updateSouthFace = getSouthFaceFromOrder(order);

        const sfSf = southFace!.sectionFragments;
        const sfSfWindowInset = sfSf.filter(x=> x.sectionFragmentType === SectionFragmentType.Frame);
        const sfSfAreaFragment = sfSf.filter(x=> x.sectionFragmentType === SectionFragmentType.AreaFragment);

        const childIds = sfSf.flatMap(x=> x.childSectionFragmentsIds);

        childIds.forEach(cId => {
          const cEntity = sfSf.find(x=> x.id === cId);
          if(!cEntity) {
            console.error("We are putting an order that refers to areadfragment childIds that do no exist at this time, this is NOT goated.")
          }
        });

        return {
          order,
          modified: true,
        };
      }

      throw new Error(`PutOrderService: putOrder resulted in ${response.status}: ${response.statusText}`);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Order Request canceled due newer one start');
        return null;
      } else {
        throw error;
      }
    }
  }
}

export interface PutOrderResult {
  order: OrderDto;
  modified: boolean;
}
